import { type RefObject, useCallback, useEffect, useRef } from 'react';

type JumpableHiker = {
  jump: () => void;
  updateGravity: (msPressed: number) => void;
};

const useJumpControls = (hikerRef: RefObject<JumpableHiker>, enabled: boolean, onJump?: () => void) => {
  const spaceBarPressedTime = useRef<number>(null);

  const jump = useCallback(() => {
    spaceBarPressedTime.current = Date.now();
    // Jump as soon as the space bar is pressed
    hikerRef.current?.jump();
    onJump?.();
  }, [hikerRef, onJump]);

  const release = useCallback(() => {
    if (spaceBarPressedTime.current) {
      const msPressed = Date.now() - spaceBarPressedTime.current;
      // Update the gravity based on the time the space bar was pressed
      // This creates a slightly less smooth jump, but it allows us to jump as soon as the space bar is pressed
      hikerRef.current?.updateGravity(msPressed);
    }
    spaceBarPressedTime.current = null;
  }, [hikerRef]);

  useEffect(() => {
    // Handle keyboard events when the game is running
    if (!enabled) {
      return () => {};
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Space' && !event.repeat) {
        event.preventDefault();
        event.stopPropagation();
        jump();
      }
    };
    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'Space') {
        event.preventDefault();
        event.stopPropagation();
        release();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    return () => {
      spaceBarPressedTime.current = null;
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [enabled, jump, release]);

  return { jump, release };
};

export default useJumpControls;
